import React from "react";
import {useNavigate} from "react-router-dom";

import {Badge} from "./ui/badge";
import {cn} from "lib/utils";

/**
 * A story as it appears on a section front.
 *
 * The old card buried every headline under a photograph and a black gradient,
 * which made every story weigh exactly the same. Print solves this with type:
 * the picture supports the headline, it does not host it. Three weights of
 * story share one component:
 *
 *   lead     - the story above the fold. Large serif, standfirst, big picture.
 *   standard - the body of the section front.
 *   compact  - a list row; thumbnail optional, headline does the work.
 */

const VARIANT = {
  lead: {
    figure: "aspect-[16/9]",
    headline: "text-h1 sm:text-lede",
    showStandfirst: true,
    clamp: "line-clamp-3",
  },
  standard: {
    figure: "aspect-[3/2]",
    headline: "text-h3",
    showStandfirst: false,
    clamp: "line-clamp-3",
  },
  compact: {
    figure: "aspect-square",
    headline: "text-h4",
    showStandfirst: false,
    clamp: "line-clamp-2",
  },
};

const StoryCard = React.forwardRef(
  (
    {
      title,
      description,
      image,
      category,
      duration,
      className,
      onAssetClick,
      sources,
      id,
      isFirstItem,
      variant,
      children,
    },
    ref
  ) => {
    const navigate = useNavigate();
    const kind = variant || (isFirstItem ? "lead" : "standard");
    const spec = VARIANT[kind] || VARIANT.standard;
    const isCompact = kind === "compact";

    const open = () => {
      onAssetClick?.();
      navigate(`/article/${id}`);
    };

    const onKeyDown = (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        open();
      }
    };

    const byline = [sources?.length ? sources.join(", ") : null, duration]
      .filter(Boolean)
      .join(" · ");

    const figure = image ? (
      <div
        className={cn(
          "photo-inset relative overflow-hidden rounded-sm bg-surface-2",
          spec.figure,
          isCompact && "w-[84px] shrink-0 sm:w-[104px]"
        )}
      >
        <img
          src={image}
          alt=""
          loading="lazy"
          className="size-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]"
        />
      </div>
    ) : null;

    const copy = (
      <div className={cn("flex min-w-0 flex-col", isCompact ? "gap-1" : "gap-2")}>
        {category ? (
          <Badge variant="kicker" className={isCompact ? "text-[0.625rem]" : ""}>
            {category}
          </Badge>
        ) : null}

        <h3
          className={cn(
            "font-serif font-bold text-ink",
            spec.headline,
            spec.clamp,
            "decoration-masthead/40 underline-offset-[3px] group-hover:underline"
          )}
        >
          {title}
        </h3>

        {spec.showStandfirst && description ? (
          <p className="text-column text-standfirst text-ink-secondary line-clamp-3">
            {description}
          </p>
        ) : null}

        {byline ? (
          <p className="font-sans text-meta text-ink-muted">{byline}</p>
        ) : null}

        {children}
      </div>
    );

    return (
      <article
        ref={ref}
        role="link"
        tabIndex={0}
        onClick={open}
        onKeyDown={onKeyDown}
        className={cn(
          "group cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:ring-offset-paper",
          isCompact ? "flex gap-3" : "flex flex-col gap-3",
          className
        )}
      >
        {isCompact ? (
          <>
            {copy}
            {figure}
          </>
        ) : (
          <>
            {figure}
            {copy}
          </>
        )}
      </article>
    );
  }
);

StoryCard.displayName = "StoryCard";

export default StoryCard;
